import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useGlobalStore } from '../../stores/globalStore';

/**
 * Shown in place of the page when signing in could not finish: the user's facility list or
 * security role could not be read (globalStore.sessionLoadError). Nothing is shown until they
 * load, because without them there is no facility the user is known to be allowed in and no
 * level to check any action against.
 */
export default function SessionLoadError({ message }: { message: string }) {
  const retry = useGlobalStore((s) => s.retrySessionLoad);
  // Refused on purpose (no matching security role): the message says what to do; trying again won't help.
  const refused = useGlobalStore((s) => s.sessionLoadRefused);
  if (refused) {
    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          height: '100%',
          maxWidth: 560,
          margin: '0 auto',
          padding: 24,
          textAlign: 'center',
        }}
      >
        <i className="bi bi-person-lock" style={{ fontSize: 40, color: 'var(--color-warning)' }} aria-hidden="true" />
        <h2 style={{ margin: 0, color: 'var(--text-primary)' }}>You can't sign in to this facility yet</h2>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--font-size-base)' }}>{message}</p>
      </div>
    );
  }
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        height: '100%',
        maxWidth: 560,
        margin: '0 auto',
        padding: 24,
        textAlign: 'center',
      }}
    >
      <i className="bi bi-exclamation-triangle" style={{ fontSize: 40, color: 'var(--color-warning)' }} aria-hidden="true" />
      <h2 style={{ margin: 0, color: 'var(--text-primary)' }}>Could not finish signing in</h2>
      <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--font-size-base)' }}>
        {message} Pages stay closed until it loads. Check your connection and try again, or contact
        your administrator if this keeps happening.
      </p>
      <Button variant="contained" size="small" onClick={retry} sx={{ textTransform: 'none' }}>
        Try again
      </Button>
    </div>
  );
}

/** Inline "permissions are loading" placeholder, for guards that render before the role arrives. */
export function PermissionsLoading() {
  return (
    <div
      role="status"
      aria-label="Loading permissions"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '80px 24px' }}
    >
      <CircularProgress size={36} aria-hidden="true" />
      <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)' }}>Loading...</span>
    </div>
  );
}
